'use client'

/**
 * Client-direct WAPI calls for auth mutations (ui-react parity). Authenticate
 * with the user's token in the `X-TOKEN` header; no Nexus credential is involved.
 * `urlRoot` is the public WAPI base (e.g. https://api.wanderu.com/v2/, trailing slash).
 *
 * Every call answers with a `Result` and never rejects, so a caller cannot reach the
 * value without passing the failure (`services.md#services-return-result`).
 */

// The `result` submodule, not the `true-myth` root: the root is a namespace barrel over
// Maybe, Task and toolbelt (see its own `dist/index.js`), and true-myth declares no
// `sideEffects: false` to let a bundler drop them. This module reaches the client bundle
// through SessionProvider, so whatever it pulls is paid for on every page that mounts one.
import Result from 'true-myth/result'

// Bound every request so a hung WAPI socket aborts (surfacing as a `network` Err the
// caller treats as fail-safe) rather than stalling the sequential auto-login chain.
const WAPI_TIMEOUT_MS = 8000

/**
 * Why a client-direct WAPI call failed, as a low-cardinality discriminator safe to put in
 * log metadata (`observability.md#low-cardinality-log-metadata`): `http` — WAPI answered
 * non-2xx, and `statusCode` carries it; `malformed` — a 2xx whose body lacks the fields the
 * call needs; `network` — the request never completed (offline, DNS, CORS, or the timeout
 * abort), with the underlying failure preserved as `cause`.
 */
export type ClientWapiErrorCode = 'http' | 'malformed' | 'network'

/** A failed client-direct WAPI call. Carries no token, body, or other user data. */
export class ClientWapiError extends Error {
    readonly name = 'ClientWapiError'

    constructor(
        message: string,
        readonly code: ClientWapiErrorCode,
        readonly statusCode?: number,
        options?: ErrorOptions,
    ) {
        super(message, options)
    }

    /** WAPI answered, but not with a 2xx. */
    static fromResponse(operation: string, response: Response): ClientWapiError {
        return new ClientWapiError(
            `WAPI ${operation} failed: ${String(response.status)}`,
            'http',
            response.status,
        )
    }

    /** The request never produced a response — a rejected `fetch` or an unreadable body. */
    static fromError(operation: string, cause: unknown): ClientWapiError {
        return new ClientWapiError(`WAPI ${operation} did not complete`, 'network', undefined, {
            cause,
        })
    }
}

/** WAPI app credentials (clientId/buildId/clientName) — public app identifiers, sent
 * in the federated auth body (ui-react parity). Not a user secret. */
export interface WapiClientCredentials {
    clientId: string
    buildId: string
    clientName: string
}

/** The session minted by WAPI from a federated credential. Token is held by the caller
 * (written to the mirror); it is never placed on the client-safe SessionContext. */
export interface FederatedSession {
    token: string
    identityId: string | null
    sessionId: string
    /** WAPI `expDt` as returned — an ISO-8601 string in practice (parsed by `readMirror`). */
    expiresAt: number | string | null
}

/** The envelope `auth.json` and `refresh.json` both answer a minted session with. */
interface SessionEnvelope {
    result?: {
        token?: string
        identityId?: string
        sessionId?: string
        expDt?: number | string
    }
}

function readSession(
    data: SessionEnvelope,
    operation: string,
): Result<FederatedSession, ClientWapiError> {
    const result = data.result
    if (!result?.token || !result.sessionId) {
        return Result.err(
            new ClientWapiError(`WAPI ${operation}: response missing token/sessionId`, 'malformed'),
        )
    }

    return Result.ok({
        token: result.token,
        identityId: result.identityId ?? null,
        sessionId: result.sessionId,
        expiresAt: result.expDt ?? null,
    })
}

/**
 * Exchange a federated provider credential (Google) for a WAPI session, ui-react
 * parity: `POST {urlRoot}auth.json` with `{ credentials: { ...wapiCreds, provider, token,
 * type: 'federated' }, meta }`, `Content-Type: text/plain`, **no** `X-TOKEN` (there is no
 * session yet).
 */
export async function federatedLogin(opts: {
    urlRoot: string
    credentials: WapiClientCredentials
    provider: 'google'
    token: string
}): Promise<Result<FederatedSession, ClientWapiError>> {
    try {
        const response = await fetch(`${opts.urlRoot}auth.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify({
                credentials: {
                    ...opts.credentials,
                    provider: opts.provider,
                    token: opts.token,
                    type: 'federated',
                },
                meta: {},
            }),
            signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
        })

        if (!response.ok) {
            return Result.err(ClientWapiError.fromResponse('federated auth', response))
        }

        return readSession((await response.json()) as SessionEnvelope, 'federated auth')
    } catch (error) {
        return Result.err(ClientWapiError.fromError('federated auth', error))
    }
}

/**
 * Refresh an expired session token, ui-react parity (`AccountService.refreshToken`):
 * `POST {urlRoot}refresh.json` with `{ email, token, sessionId?, meta }`, `Content-Type:
 * text/plain`, **no** `X-TOKEN` (the expired token in the body is the credential). Returns
 * the refreshed session. Cognito-only — federated sessions re-restore via their SDK, not
 * refresh.
 */
export async function refreshRequest(opts: {
    urlRoot: string
    email: string
    token: string
    sessionId?: string
}): Promise<Result<FederatedSession, ClientWapiError>> {
    try {
        const response = await fetch(`${opts.urlRoot}refresh.json`, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify({
                email: opts.email,
                token: opts.token,
                ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
                meta: {},
            }),
            signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
        })

        if (!response.ok) {
            return Result.err(ClientWapiError.fromResponse('refresh', response))
        }

        return readSession((await response.json()) as SessionEnvelope, 'refresh')
    } catch (error) {
        return Result.err(ClientWapiError.fromError('refresh', error))
    }
}

/** The logged-in user's profile (subset Nexus needs for the navbar greeting). */
export interface UserInfo {
    firstName?: string
    lastName?: string
    email?: string
}

/**
 * Fetch the logged-in user's profile, ui-react parity (`AccountService.getUserInfo`):
 * `GET {urlRoot}userinfo.json` with the user token (`X-TOKEN`). The name is **not** in the
 * mirror, so this is how the navbar greets by name. Returns `{ firstName, lastName, email }`
 * (other profile fields are ignored).
 */
export async function getUserInfo(opts: {
    urlRoot: string
    token: string
}): Promise<Result<UserInfo, ClientWapiError>> {
    try {
        const response = await fetch(`${opts.urlRoot}userinfo.json`, {
            method: 'GET',
            headers: { 'X-TOKEN': opts.token },
            signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
        })

        if (!response.ok) {
            return Result.err(ClientWapiError.fromResponse('userinfo', response))
        }

        const data = (await response.json()) as {
            result?: { firstName?: string; lastName?: string; email?: string }
        }
        const result = data.result ?? {}
        return Result.ok({
            ...(typeof result.firstName === 'string' ? { firstName: result.firstName } : {}),
            ...(typeof result.lastName === 'string' ? { lastName: result.lastName } : {}),
            ...(typeof result.email === 'string' ? { email: result.email } : {}),
        })
    } catch (error) {
        return Result.err(ClientWapiError.fromError('userinfo', error))
    }
}

/** Invalidate the session server-side: POST `/v2/logout.json` with the user token. */
export async function logoutRequest(
    urlRoot: string,
    token: string,
): Promise<Result<void, ClientWapiError>> {
    try {
        const response = await fetch(`${urlRoot}logout.json`, {
            method: 'POST',
            headers: {
                'X-TOKEN': token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
            signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
        })

        return response.ok
            ? Result.ok(undefined)
            : Result.err(ClientWapiError.fromResponse('logout', response))
    } catch (error) {
        return Result.err(ClientWapiError.fromError('logout', error))
    }
}

/**
 * POST a `user:action:identify` event to `/v2/events.json` with the user token, at
 * ui-react parity. `data` carries the identity fields (cognito_identity_id, email,
 * source, sp_domain_id, sp_network_id, …).
 */
export async function identifyRequest(opts: {
    urlRoot: string
    token: string
    data: Record<string, unknown>
}): Promise<Result<void, ClientWapiError>> {
    try {
        const response = await fetch(`${opts.urlRoot}events.json`, {
            method: 'POST',
            headers: {
                'X-TOKEN': opts.token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                event: 'user:action:identify',
                event_timestamp: Date.now(),
                data: opts.data,
            }),
            signal: AbortSignal.timeout(WAPI_TIMEOUT_MS),
        })

        return response.ok
            ? Result.ok(undefined)
            : Result.err(ClientWapiError.fromResponse('identify', response))
    } catch (error) {
        return Result.err(ClientWapiError.fromError('identify', error))
    }
}
